import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import ActionBar from "../../common/ActionBar";
import { OuterContainer } from "../../EventCreation/styles";
import { Formik } from "formik";
import styled from "styled-components";
import theme from "../../../assets/theme";
import { PrimaryCTAButton } from "../../common/Buttons";
import FormField, { ButtonContainer, FormControl } from "../FormInputs";
import EventService from "../../../services/EventService";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import { navigate } from "@reach/router";
import { input_fonts } from "../../../helpers/constants";
import _ from "lodash";

const ThemeSettings = (id) => {
  const themes = useTheme();
  const isBigScreen = useMediaQuery(themes.breakpoints.up(640));
  const [info, setInfo] = useState();
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [menuList, setMenuList] = useState([]);
  const [clickedButton, setClickedButton] = useState("");

  const general_inputs = [
    {
      name: "titleTextColor",
      label: "Title Text Color",
      type: "color",
    },
    {
      name: "titleTextFontSize",
      label: "Title Text Font Size",
      type: "number",
      placeholder: "Enter title text font size",
    },
    {
      name: "titleTextFont",
      label: "Title Text Font",
      type: "select",
      menuItems: input_fonts,
    },
    {
      name: "bodyBackgroundColor",
      label: "Body Background Color",
      type: "color",
    },
    {
      name: "bodyTextBackgroundColor",
      label: "Body Text Background Color",
      type: "color",
    },
    {
      name: "bodyTextColor",
      label: "Body Text Color",
      type: "color",
    },
    {
      name: "labelTextColor",
      label: "Label Text Color",
      type: "color",
    },
    {
      name: "bodyTextFont",
      label: "Body Text Font",
      type: "select",
      menuItems: input_fonts,
    },
    {
      name: "backgroundImage",
      label: "Background Image",
      type: "upload",
      accept: "image/*",
    },
    {
      name: "animateBackgroundImage",
      label: "Animate Background Image",
      type: "switch",
    },
  ];

  const navigation_bar_inputs = [
    {
      name: "navBarBgColor",
      label: "Nav Bar BG Color",
      type: "color",
    },
    {
      name: "activeMenuButtonBgColor",
      label: "Active Menu Button BG Color",
      type: "color",
    },
    {
      name: "activeMenuButtonTextColor",
      label: "Active Menu Button Text Color",
      type: "color",
    },
    {
      name: "menuButtonBgColor",
      label: "Menu Button BG Color",
      type: "color",
    },
    {
      name: "menuButtonTextColor",
      label: "Menu Button Text Color",
      type: "color",
    },
    {
      name: "menuButtonHoverColor",
      label: "Menu Button Hover Color",
      type: "color",
    },
    {
      name: "menuButtonHoverTextColor",
      label: "Menu Button Hover Text Color",
      type: "color",
    },
    {
      name: "navBarTreatment",
      label: "Nav Bar Treatment",
      type: "radio",
      menuItems: [
        {
          label: "Always Visible",
          name: "alwaysVisible",
          value: "alwaysVisible",
        },
        { label: "Burger Menu", name: "burgerMenu", value: "burgerMenu" },
      ],
    },
    {
      name: "burgerMenuPlacement",
      label: "Burger Menu Placement",
      type: "radio",
      menuItems: [
        { label: "Top Left", name: "topLeft", value: "topLeft" },
        { label: "Top Right", name: "topRight", value: "topRight" },
        { label: "Buttom Left", name: "buttomLeft", value: "buttomLeft" },
        { label: "Bottom Right", name: "bottomRight", value: "bottomRight" },
      ],
    },
  ];

  const header_inputs = [
    {
      name: "bannerBackgroundColor",
      label: "Banner Background Color",
      type: "color",
    },
    {
      name: "eventTitleColor",
      label: "Event Title Color",
      type: "color",
    },
    {
      name: "eventTitleShadowColor",
      label: "Event Title Shadow Color",
      type: "color",
    },
    {
      name: "eventTitleImage",
      label: "Event Title Image",
      type: "upload",
      accept: "image/*",
    },
    {
      name: "logoImage",
      label: "Logo Image",
      type: "upload",
      accept: "image/*",
    },
    {
      name: "logoWidth",
      label: "Logo Width (px)",
      type: "number",
    },
    {
      name: "logoHeight",
      label: "Logo Height (px)",
      type: "number",
    },
    {
      name: "logoPlacement",
      label: "Logo Placement",
      type: "radio",
      menuItems: [
        { label: "Left", name: "left", value: "left" },
        { label: "Right", name: "right", value: "right" },
      ],
    },
  ];

  const footer_inputs = [
    {
      name: "footerBackgroundColor",
      label: "Footer Background Color",
      type: "color",
    },
    {
      name: "footerTextColor",
      label: "Footer Text Color",
      type: "color",
    },
    {
      name: "poweredBy",
      label: "Powered By",
      type: "autoSelect",
      menuItems: [{ label: "Dsnl", value: "dsnl", name: "dsnl" }],
    },
    {
      name: "footerLogo",
      label: "Footer Logo",
      type: "upload",
      accept: "image/*",
    },
    {
      name: "poweredByFormat",
      label: "Powered By Format",
      type: "radio",
      menuItems: [
        { label: "Logo", name: "logo", value: "logo" },
        { label: "Text Only", name: "textOnly", value: "textOnly" },
      ],
    },
    {
      name: "border",
      label: "Border",
      type: "checkbox",
      checkItems: [
        { label: "Top", name: "top", value: "top" },
        { label: "Bottom", name: "bottom", value: "bottom" },
      ],
    },
    {
      name: "borderColor",
      label: "Border Color",
      type: "color",
    },
  ];

  const buttons = [
    {
      label: "SAVE",
      color: "default",
      primary: true,
      name: "save",
    },
    {
      label: "SAVE & NEXT",
      color: "primary",
      secondary: true,
      name: "save&next",
    },
    {
      label: "CLEAR FORM",
      color: "secondary",
      clearForm: true,
    },
  ];

  useEffect(() => {
    async function getEventInfo() {
      try {
        setLoader({ state: true, message: "Fetching event details..." });
        const response = await EventService.getEvent(id?.id, "theme");
        const templateResponse = await EventService.getCopyFromTemplate(
          parseInt(response?.orgId),
        );
        setMenuList(
          templateResponse.queryBuilder.map((arr) => ({
            label: `${arr.eventName}-${arr?.eventTitle}`,
            name: `${arr.eventName}-${arr?.eventTitle}`,
            value: arr?.eventId,
          })),
        );
        setInfo(response);
      } catch (err) {
        const message = getErrorMessage(err);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
        window.scrollTo(0, 0);
      }
    }
    if (id?.id) {
      getEventInfo();
    } else {
      window.scrollTo(0, 0);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getEventInfo(id, updatedValue) {
    try {
      setLoader({ state: true, message: "Fetching theme..." });
      const response = await EventService.getEvent(id, "theme");
      setInfo({
        ...response,
        themeGeneral: {
          ...response.themeGeneral,
          copyFromTemplate: updatedValue
            ? updatedValue
            : response?.themeGeneral?.copyFromTemplate,
        },
      });
      // setInfo(response);
    } catch (err) {
      const message = getErrorMessage(err);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  /**
   * @description theme settings submit function
   */

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "please wait..." });
      const payload = {
        ...values,
      };
      const response = await EventService.updateEvent(id?.id, payload);
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
      if (clickedButton === "save") {
        getEventInfo(id?.id);
      } else {
        navigate(`/dashboard/eventmanager/speakers/${id?.id}`);
      }
    } catch (err) {
      enqueueSnackbar(err?.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "event_manager":
        navigate(`/dashboard/eventmanager/home`);
        break;
      case "event_settings":
        id?.id
          ? navigate(`/dashboard/eventmanager/event-settings/${id?.id}`)
          : navigate(`/dashboard/eventmanager/event-settings`);
        break;
      default:
        break;
    }
  };

  /**
   * @description handle button click
   */

  const handleButtonClick = (button, resetForm, handleSubmit) => {
    if (button.clearForm) {
      if (id?.id) {
        getEventInfo(id?.id);
      } else {
        resetForm();
      }
    } else {
      setClickedButton(button.name); // Store clicked button
      handleSubmit();
    }
  };

  return (
    <OuterContainer disabled={false}>
      <ActionBar
        breadcrumbs={
          id?.id
            ? [
                "Event Manager",
                "Event Settings",
                "Theme Settings",
                info?.eventName
                  ? `${info?.eventName} (${info?.eventDate})`
                  : "",
              ]
            : ["Event Manager", "Event Settings", "Theme Settings"]
        }
        {...{ onBreadcrumbClick }}
      />
      <Container>
        <Formik
          initialValues={{
            themeGeneral: {
              copyFromTemplate: info?.themeGeneral?.copyFromTemplate || "",
              titleTextColor: info?.themeGeneral?.titleTextColor || "#F1F0E9",
              titleTextFontSize: info?.themeGeneral?.titleTextFontSize || "",
              titleTextFont:
                info?.themeGeneral?.titleTextFont ||
                "openSans-SemiBold, sans-serif",
              bodyBackgroundColor:
                info?.themeGeneral?.bodyBackgroundColor || "#F1F0E9",
              bodyTextBackgroundColor:
                info?.themeGeneral?.bodyTextBackgroundColor || "#F1F0E9",
              bodyTextColor: info?.themeGeneral?.bodyTextColor || "#F1F0E9",
              labelTextColor: info?.themeGeneral?.labelTextColor || "#8294a5",
              bodyTextFont:
                info?.themeGeneral?.bodyTextFont ||
                "openSans-SemiBold, sans-serif",
              backgroundImage: info?.themeGeneral?.backgroundImage || "",
              animateBackgroundImage:
                info?.themeGeneral?.animateBackgroundImage || "",
            },
            themeHeader: {
              bannerBackgroundColor:
                info?.themeHeader?.bannerBackgroundColor || "#F1F0E9",
              eventTitleColor: info?.themeHeader?.eventTitleColor || "#F1F0E9",
              eventTitleShadowColor:
                info?.themeHeader?.eventTitleShadowColor || "#F1F0E9",
              eventTitleImage: info?.themeHeader?.eventTitleImage || "",
              logoImage: info?.themeHeader?.logoImage || "",
              logoWidth: info?.themeHeader?.logoWidth || "144",
              logoHeight: info?.themeHeader?.logoHeight || "60",
              logoPlacement: info?.themeHeader?.logoPlacement || "left",
            },
            themeNavigationBar: {
              navBarBgColor:
                info?.themeNavigationBar?.navBarBgColor || "#F1F0E9",
              activeMenuButtonBgColor:
                info?.themeNavigationBar?.activeMenuButtonBgColor || "#F1F0E9",
              activeMenuButtonTextColor:
                info?.themeNavigationBar?.activeMenuButtonTextColor ||
                "#F1F0E9",
              menuButtonBgColor:
                info?.themeNavigationBar?.menuButtonBgColor || "#F1F0E9",
              menuButtonTextColor:
                info?.themeNavigationBar?.menuButtonTextColor || "#F1F0E9",
              menuButtonHoverColor:
                info?.themeNavigationBar?.menuButtonHoverColor || "#F1F0E9",
              menuButtonHoverTextColor:
                info?.themeNavigationBar?.menuButtonHoverTextColor || "#F1F0E9",
              navBarTreatment:
                info?.themeNavigationBar?.navBarTreatment || "alwaysVisible",
              burgerMenuPlacement:
                info?.themeNavigationBar?.burgerMenuPlacement || "topRight",
            },
            themeFooter: {
              footerBackgroundColor:
                info?.themeFooter?.footerBackgroundColor || "#F1F0E9",
              footerTextColor: info?.themeFooter?.footerTextColor || "#F1F0E9",
              poweredBy: info?.themeFooter?.poweredBy || "",
              poweredByFormat: info?.themeFooter?.poweredByFormat || "logo",
              border: info?.themeFooter?.border || "bottom",
              borderColor: info?.themeFooter?.borderColor || "#F1F0E9",
              footerLogo: info?.themeFooter?.footerLogo || "",
            },
          }}
          enableReinitialize
          onSubmit={onFormSubmit}
          validationSchema={""}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            handleChange,
            setFieldValue,
            resetForm,
          }) => (
            <FormContainer>
              <Container>
                <Paper elevation={3} style={{ padding: "16px" }}>
                  <Heading>Theme Settings</Heading>
                  <Grid container spacing={2} style={{ marginTop: "16px" }}>
                    <Grid item xs={isBigScreen ? 12 : 12}>
                      <Paper
                        style={{ padding: 16, border: "1px solid #e1e3ee" }}
                      >
                        <GridLabel>Event Info</GridLabel>
                        <Grid
                          container
                          spacing={2}
                          style={{
                            marginTop: "16px",
                            marginLeft: isBigScreen && "10px",
                          }}
                        >
                          {[
                            {
                              name: "copyFromTemplate",
                              label: "Copy From Template",
                              type: "select",
                              menuItems: menuList,
                            },
                          ].map((item, index) => (
                            <Grid
                              item
                              xs={isBigScreen ? 4 : 12}
                              key={item.name}
                              style={styles.gridItem}
                              className="disabledArrows"
                            >
                              <FormField
                                {...{
                                  item,
                                  values,
                                  setFieldValue,
                                  index,
                                  errors,
                                }}
                                handleChange={(event) => {
                                  setFieldValue(
                                    `themeGeneral.${item.name}`,
                                    event.target.value,
                                  );
                                  getEventInfo(
                                    event.target.value,
                                    event.target.value,
                                  );
                                }}
                                accept={item.accept}
                                index={index}
                                menuItems={item.menuItems}
                                value={values.themeGeneral[item.name]}
                                name={`themeGeneral.${item.name}`}
                                touched={`themeGeneral.${item.name}`}
                              />
                            </Grid>
                          ))}

                          {general_inputs.map((item, index) => (
                            <Grid
                              item
                              xs={isBigScreen ? 4 : 12}
                              key={item.name}
                              style={styles.gridItem}
                            >
                              <FormField
                                {...{
                                  item,
                                  handleChange,
                                  values,
                                  setFieldValue,
                                  index,
                                  errors,
                                }}
                                accept={item.accept}
                                index={index}
                                menuItems={item.menuItems}
                                value={values.themeGeneral[item.name]}
                                name={`themeGeneral.${item.name}`}
                                touched={`themeGeneral.${item.name}`}
                                width={"210px"}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                    <Grid item xs={isBigScreen ? 12 : 12}>
                      <Paper
                        style={{ padding: 16, border: "1px solid #e1e3ee" }}
                      >
                        <GridLabel>Navigation Bar</GridLabel>
                        <Grid
                          container
                          spacing={2}
                          style={{
                            marginTop: "16px",
                            marginLeft: isBigScreen && "10px",
                          }}
                        >
                          {navigation_bar_inputs.map((item, index) => (
                            <Grid
                              item
                              xs={isBigScreen ? 4 : 12}
                              key={item.name}
                              style={styles.gridItem}
                            >
                              <FormField
                                {...{
                                  item,
                                  handleChange,
                                  values,
                                  setFieldValue,
                                  errors,
                                }}
                                index={index}
                                value={values.themeNavigationBar[item.name]}
                                name={`themeNavigationBar.${item.name}`}
                                touched={`themeNavigationBar.${item.name}`}
                                direction={"row"}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                    <Grid item xs={isBigScreen ? 12 : 12}>
                      <Paper
                        style={{ padding: 16, border: "1px solid #e1e3ee" }}
                      >
                        <GridLabel>Header</GridLabel>
                        <Grid
                          container
                          spacing={2}
                          style={{
                            marginTop: "16px",
                            marginLeft: isBigScreen && "10px",
                          }}
                        >
                          {header_inputs.map((item, index) => (
                            <Grid
                              item
                              xs={isBigScreen ? 4 : 12}
                              key={item.name}
                              style={styles.gridItem}
                              className="disabledArrows"
                            >
                              <FormField
                                {...{
                                  item,
                                  handleChange,
                                  values,
                                  setFieldValue,
                                  errors,
                                }}
                                accept={item.accept}
                                index={index}
                                direction={"row"}
                                value={values.themeHeader[item.name]}
                                name={`themeHeader.${item.name}`}
                                touched={`themeHeader.${item.name}`}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                    <Grid item xs={isBigScreen ? 12 : 12}>
                      <Paper
                        style={{ padding: 16, border: "1px solid #e1e3ee" }}
                      >
                        <GridLabel>Footer</GridLabel>
                        <Grid
                          container
                          spacing={2}
                          style={{
                            marginTop: "16px",
                            marginLeft: isBigScreen && "10px",
                          }}
                        >
                          {footer_inputs.map((item, index) => (
                            <Grid
                              item
                              xs={isBigScreen ? 4 : 12}
                              key={item.name}
                              style={styles.gridItem}
                            >
                              <FormField
                                {...{
                                  item,
                                  handleChange,
                                  values,
                                  setFieldValue,
                                  errors,
                                }}
                                index={index}
                                menuItems={item.menuItems}
                                value={values.themeFooter[item.name]}
                                name={`themeFooter.${item.name}`}
                                touched={`themeFooter.${item.name}`}
                                direction={"row"}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                  <FormControl>
                    <ButtonContainer>
                      {buttons.map((button) => (
                        <PrimaryCTAButton
                          style={{ width: "auto", marginLeft: 8 }}
                          onClick={() =>
                            handleButtonClick(button, resetForm, handleSubmit)
                          }
                        >
                          {button.label}
                        </PrimaryCTAButton>
                      ))}
                    </ButtonContainer>
                  </FormControl>
                </Paper>
              </Container>
            </FormContainer>
          )}
        </Formik>
      </Container>
    </OuterContainer>
  );
};

const styles = {
  container: {
    backgroundColor: "#A9A9A9", // gray background
    padding: "20px",
    borderRadius: "8px",
  },
  gridItem: {
    display: "flex",
    justifyContent: "space-between",
  },
  label: {
    fontWeight: "bold",
  },
};

export default ThemeSettings;

const Heading = styled(Typography)(() => ({
  backgroundColor: "#00aeef",
  color: "#fff",
  padding: "8px",
  fontFamily: theme.fonts.primaryFontBold,
  borderRadius: "5px",
}));

const FormContainer = styled.form`
  /* width: 907px; */

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  @media ${theme?.breakpoints?.md_up} {
    width: 100%;
  }
`;

const Container = styled.div`
  padding: 20px 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  & .delete {
    display: flex;
    cursor: pointer;
  }
  @media ${theme?.breakpoints?.sm_up} {
  }
`;

const GridLabel = styled(Typography)(() => ({
  width: "250px",
  color: " #00838c",
  fontSize: "15px",
  marginTop: "10px",
  marginBottom: "10px",
  marginLeft: "15px",
  fontFamily: theme.fonts.primaryFontSemiBold,
}));
